import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Navigation from "@/components/Navigation";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Pencil, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const [tracks, setTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newTrack, setNewTrack] = useState({ title: "", playlist: "", release_date: "", thumbnail: "", duration: "" });
  const fileInputRef = useRef(null);

  const fetchTracks = async () => {
    try {
      const res = await axios.get(`${API}/catalog?sort=alpha`);
      setTracks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPlaylists = async () => {
    try {
      const res = await axios.get(`${API}/catalog/playlists`);
      setPlaylists(res.data.playlists || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchTracks(); fetchPlaylists(); }, []);

  const handleAdd = async () => {
    if (!newTrack.title) { toast.error("TITLE REQUIRED"); return; }
    try {
      await axios.post(`${API}/catalog`, newTrack);
      toast.success("TRACK ADDED");
      setNewTrack({ title: "", playlist: "", release_date: "", thumbnail: "", duration: "" });
      setShowAdd(false);
      fetchTracks();
      fetchPlaylists();
    } catch (e) { toast.error("FAILED TO ADD"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/catalog/${id}`);
      toast.success("TRACK DELETED");
      fetchTracks();
    } catch (e) { toast.error("DELETE FAILED"); }
  };

  const handleEditStart = (track) => {
    setEditingId(track.id);
    setEditData({ title: track.title, playlist: track.playlist, release_date: track.release_date, thumbnail: track.thumbnail, duration: track.duration });
  };

  const handleEditSave = async () => {
    try {
      await axios.put(`${API}/catalog/${editingId}`, editData);
      toast.success("TRACK UPDATED");
      setEditingId(null);
      fetchTracks();
      fetchPlaylists();
    } catch (e) { toast.error("UPDATE FAILED"); }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/catalog/csv-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${res.data.count} TRACKS IMPORTED`);
      fetchTracks();
      fetchPlaylists();
    } catch (e) { toast.error("CSV UPLOAD FAILED"); }
    e.target.value = "";
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] noise-bg">
      <Navigation />

      <div className="max-w-5xl mx-auto p-6 md:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-xs tracking-[0.3em] text-[#FF3333] uppercase font-bold" style={{ fontFamily: "'Azeret Mono', monospace" }}>
              // ADMIN_CONTROL
            </span>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#E5E5E5] mt-2" style={{ fontFamily: "'Azeret Mono', monospace" }}>
              MANAGE TRANSMISSIONS
            </h1>
            <p className="text-[10px] text-[#444] mt-1">{tracks.length} SIGNALS IN DATABASE</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="csv-upload-btn"
              className="flex items-center gap-2 border border-white/20 px-4 py-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#888] hover:text-[#E5E5E5] hover:border-[#E5E5E5]"
              style={{ fontFamily: "'Azeret Mono', monospace" }}
            >
              <Upload size={12} /> CSV UPLOAD
            </button>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
            <button
              onClick={() => setShowAdd(!showAdd)}
              data-testid="add-track-btn"
              className="flex items-center gap-2 border border-[#E5E5E5] px-4 py-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#E5E5E5] hover:bg-[#FF3333] hover:text-[#050505] hover:border-[#FF3333]"
              style={{ fontFamily: "'Azeret Mono', monospace" }}
            >
              <Plus size={12} /> ADD TRACK
            </button>
          </div>
        </div>

        {/* CSV Info */}
        <div className="border border-white/10 p-4 mb-6">
          <p className="text-xs text-[#888]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            <span className="text-[#FF3333]">&gt;_</span> CSV FORMAT: title, playlist, release_date, thumbnail, duration
          </p>
          <p className="text-[10px] text-[#444] mt-1">Example row: VOID_WALKER, SIGNAL_DECAY, 2026-01-15, https://img.url/art.png, 4:18</p>
        </div>

        {/* Add Track Form */}
        {showAdd && (
          <div className="border border-[#FF3333]/30 p-6 mb-6 space-y-3" data-testid="add-track-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="TITLE *" value={newTrack.title} onChange={(e) => setNewTrack({ ...newTrack, title: e.target.value })}
                className="bg-transparent border-white/20 rounded-none text-sm text-[#E5E5E5] placeholder:text-[#333] focus:border-[#FF3333] focus-visible:ring-0" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
              <Input placeholder="PLAYLIST" value={newTrack.playlist} onChange={(e) => setNewTrack({ ...newTrack, playlist: e.target.value })}
                className="bg-transparent border-white/20 rounded-none text-sm text-[#E5E5E5] placeholder:text-[#333] focus:border-[#FF3333] focus-visible:ring-0" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
              <Input placeholder="RELEASE DATE (YYYY-MM-DD)" value={newTrack.release_date} onChange={(e) => setNewTrack({ ...newTrack, release_date: e.target.value })}
                className="bg-transparent border-white/20 rounded-none text-sm text-[#E5E5E5] placeholder:text-[#333] focus:border-[#FF3333] focus-visible:ring-0" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
              <Input placeholder="DURATION (e.g. 3:42)" value={newTrack.duration} onChange={(e) => setNewTrack({ ...newTrack, duration: e.target.value })}
                className="bg-transparent border-white/20 rounded-none text-sm text-[#E5E5E5] placeholder:text-[#333] focus:border-[#FF3333] focus-visible:ring-0" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <Input placeholder="THUMBNAIL URL" value={newTrack.thumbnail} onChange={(e) => setNewTrack({ ...newTrack, thumbnail: e.target.value })}
              className="bg-transparent border-white/20 rounded-none text-sm text-[#E5E5E5] placeholder:text-[#333] focus:border-[#FF3333] focus-visible:ring-0" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
            <div className="flex gap-2">
              <button onClick={handleAdd} className="border border-[#E5E5E5] px-6 py-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#E5E5E5] hover:bg-[#FF3333] hover:text-[#050505] hover:border-[#FF3333]" style={{ fontFamily: "'Azeret Mono', monospace" }}>
                SAVE
              </button>
              <button onClick={() => setShowAdd(false)} className="border border-white/20 px-6 py-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#888] hover:text-[#E5E5E5]" style={{ fontFamily: "'Azeret Mono', monospace" }}>
                CANCEL
              </button>
            </div>
          </div>
        )}

        {/* Track List */}
        <div className="border border-white/10">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-white/10 text-[10px] tracking-[0.15em] text-[#444] uppercase font-bold" style={{ fontFamily: "'Azeret Mono', monospace" }}>
            <div className="col-span-1">ART</div>
            <div className="col-span-3">TITLE</div>
            <div className="col-span-2">PLAYLIST</div>
            <div className="col-span-2">DATE</div>
            <div className="col-span-1">DUR</div>
            <div className="col-span-3 text-right">ACTIONS</div>
          </div>

          {loading ? (
            <p className="p-6 text-sm text-[#888] flicker">LOADING...</p>
          ) : tracks.length === 0 ? (
            <p className="p-6 text-sm text-[#444] text-center">NO TRACKS IN DATABASE</p>
          ) : (
            tracks.map((track) => (
              <div key={track.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 items-center hover:bg-white/[0.02]">
                {editingId === track.id ? (
                  <>
                    <div className="col-span-1">
                      <div className="w-8 h-8 border border-white/10 overflow-hidden">
                        <img src={editData.thumbnail || track.thumbnail || ""} alt="" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div className="col-span-3">
                      <Input value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                        className="bg-transparent border-white/20 rounded-none text-xs text-[#E5E5E5] focus:border-[#FF3333] focus-visible:ring-0 h-7 px-2" />
                    </div>
                    <div className="col-span-2">
                      <Input value={editData.playlist} onChange={(e) => setEditData({ ...editData, playlist: e.target.value })}
                        className="bg-transparent border-white/20 rounded-none text-xs text-[#E5E5E5] focus:border-[#FF3333] focus-visible:ring-0 h-7 px-2" />
                    </div>
                    <div className="col-span-2">
                      <Input value={editData.release_date} onChange={(e) => setEditData({ ...editData, release_date: e.target.value })}
                        className="bg-transparent border-white/20 rounded-none text-xs text-[#E5E5E5] focus:border-[#FF3333] focus-visible:ring-0 h-7 px-2" />
                    </div>
                    <div className="col-span-1">
                      <Input value={editData.duration} onChange={(e) => setEditData({ ...editData, duration: e.target.value })}
                        className="bg-transparent border-white/20 rounded-none text-xs text-[#E5E5E5] focus:border-[#FF3333] focus-visible:ring-0 h-7 px-2" />
                    </div>
                    <div className="col-span-3 flex justify-end gap-1">
                      <button onClick={handleEditSave} className="text-[#00FF00] hover:bg-[#00FF00]/10 p-1"><Check size={14} /></button>
                      <button onClick={() => setEditingId(null)} className="text-[#888] hover:text-[#E5E5E5] p-1"><X size={14} /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-1">
                      <div className="w-8 h-8 border border-white/10 overflow-hidden">
                        {track.thumbnail ? (
                          <img src={track.thumbnail} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center text-[8px] text-[#333]">N/A</div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-3">
                      <p className="text-xs font-bold text-[#E5E5E5] truncate" style={{ fontFamily: "'Azeret Mono', monospace" }}>{track.title}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-[#888] truncate">{track.playlist}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-[#444]">{track.release_date || "—"}</p>
                    </div>
                    <div className="col-span-1">
                      <p className="text-[10px] text-[#444]">{track.duration || "—"}</p>
                    </div>
                    <div className="col-span-3 flex justify-end gap-1">
                      <button onClick={() => handleEditStart(track)} className="text-[#888] hover:text-[#E5E5E5] p-1" data-testid={`edit-track-${track.id}`}><Pencil size={13} /></button>
                      <button onClick={() => handleDelete(track.id)} className="text-[#888] hover:text-[#FF3333] p-1" data-testid={`delete-track-${track.id}`}><Trash2 size={13} /></button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
